import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { bmxurl, ConvertDateToDB, getAccess } from 'src/globals';
import { BusyService } from '../../_services/busy.service';
import { GlobalService } from '../../_services/global.service';
import { users_returndata } from 'src/types/user_returndata';
import { booking_returndata } from 'src/types/booking_returndata';
import { tag_returndata } from 'src/types/tag_returndata';
import { tag } from 'src/types/tag';

/*
  The Booking List report cut down to the bookings that still have money owing on them, with
  what they add up to. Same request, same columns and same export as the Booking List, so the
  two can be read side by side - the only difference is which rows are kept.

  It is on two routes. /outstandingbalancereport is the report. /outstandingbalances is the
  same thing in the Views menu with a button on each row to send the customer a payment link -
  one component rather than a copy, so the two cannot drift apart. The route says which it is.
*/
@Component({
  selector: 'app-outstandingbalancereport',
  templateUrl: './outstandingbalancereport.component.html',
  styleUrls: ['./outstandingbalancereport.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class OutstandingbalancereportComponent implements OnInit {

  VenueID: string = localStorage.getItem("SmartVenueID")!;
  httpResponse: any;
  AccessAllowed: boolean = false;
  CompletedLoading: boolean = false;
  CurrencySymbol: string = localStorage.getItem("Currency") == null ? "GBP" : localStorage.getItem("Currency")!;

  FromDate: Date = new Date();
  ToDate: Date = new Date();

  tags: tag[] = [];
  chosentags: tag[] = [];

  ListBookings: any[] = [];

  guests: number = 0;
  total: number = 0;
  outstanding: number = 0;

  //Set by the route. The view can send payment links, the report only lists.
  canSendLinks: boolean = false;

  //The booking a link is being sent to, so its button can show it is busy and not be pressed twice.
  sendingTo: string = "";

  //The email the button sends: the Payment Link Template chosen in Venue Settings. Null until
  //it has been looked up, and still null if the venue has not chosen one.
  paymentLinkTemplate: any = null;

  //What the CSV export writes. The table draws its own columns.
  cols: any[] = [
    { field: 'bookingIdHR', header: 'Sales ID' },
    { field: 'createddateCSV', header: 'Created Date' },
    { field: 'startTimeCSV', header: 'Start Time' },
    { field: 'name', header: 'Name' },
    { field: 'email', header: 'Email' },
    { field: 'phone', header: 'Phone' },
    { field: 'packageName', header: 'Package' },
    { field: 'admissionsText', header: 'Admissions' },
    { field: 'guests', header: 'Guests' },
    { field: 'total', header: 'Total' },
    { field: 'outstanding', header: 'Outstanding' },
    { field: 'paymentlinksentdateCSV', header: 'Payment Link Sent' },
  ];

  constructor(
    private http: HttpClient,
    private router: Router,
    private route: ActivatedRoute,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private busyService: BusyService,
    private globalService: GlobalService) { }

  ngOnInit(): void {

    this.globalService.setSmallScreenMenu(false);

    this.canSendLinks = this.route.snapshot.data?.sendLinks == true;

    let userid = localStorage.getItem("SmartUserID");

    this.http.get(bmxurl + "User/" + userid).subscribe(response => {
      this.httpResponse = (response as users_returndata);

      if (this.httpResponse.succeeded) {
        //The same access the Booking List report asks for.
        this.AccessAllowed = getAccess("EXTRAS", this.httpResponse.data) == true;

        this.loadTags();
        this.loadgrid();

        if (this.canSendLinks) {
          this.loadPaymentLinkTemplate();
        }
      }

      this.CompletedLoading = true;
    }, error => {
      this.CompletedLoading = true;
      this.messageService.add({severity:'error', summary:'Error', detail:'Error while getting User.'});
    });
  }

  loadTags() {
    this.http.get(bmxurl + "TagList/" + this.VenueID + "/false").subscribe(response => {
      this.tags = (response as tag_returndata).data;
    }, error => {
      this.messageService.add({severity:'error', summary:'Error', detail:'Error while getting Tags.'});
    });
  }

  /*
    Venue Settings saves the chosen Payment Link Template as the PaymentLinkEmailTemplate
    setting, holding the template's id. Settings are copied into localStorage when they load,
    which is where it is read from. The template itself is fetched as well, because the send
    needs its subject and the confirmation is clearer for naming it.
  */
  private paymentLinkTemplateId(): string {
    const id = (localStorage.getItem("PaymentLinkEmailTemplate") ?? "").trim();

    return id == "" || id == "null" || id == "undefined" ? "" : id;
  }

  loadPaymentLinkTemplate() {
    const id = this.paymentLinkTemplateId();

    if (id == "") {
      return;
    }

    this.http.get(bmxurl + "/EmailTemplateList/" + this.VenueID + "/false").subscribe(response => {
      const templates: any[] = (response as any)?.data ?? [];

      this.paymentLinkTemplate = templates.find(template => template.emailtemplateid == id) ?? null;
    }, error => {
      this.messageService.add({severity:'error', summary:'Error', detail:'Error while getting the Payment Link Template.'});
    });
  }

  doneDate() {
    this.loadgrid();
  }

  editdata(bookingId: string) {
    this.router.navigateByUrl("/editbooking/" + bookingId);
  }

  loadgrid() {
    this.busyService.busy();

    //Exactly what the Booking List report sends.
    let searchCrit = {
      venueid: this.VenueID,
      startdate: ConvertDateToDB(this.FromDate),
      enddate: ConvertDateToDB(this.ToDate),
      tags: this.chosentags,
      disabled: false,
      created: true
    };

    this.http.post(bmxurl + "/BookingList", searchCrit).subscribe(response => {
      const bookings = ((response as booking_returndata).data ?? [])
        .filter((b: any) => b.inProgress == false)
        //Money owing only. A booking that is paid up, or in credit, is not what this is for.
        .filter((b: any) => (Number(b.outstanding) || 0) > 0);

      this.guests = 0;
      this.total = 0;
      this.outstanding = 0;

      bookings.forEach((booking: any) => {
        booking.createddateCSV = this.formatDateForExcel(booking.createddate);
        booking.startTimeCSV = this.formatDateForExcel(booking.startTime);
        booking.linkSent = this.hasBeenSent(booking.paymentlinksentdate);
        booking.paymentlinksentdateCSV = booking.linkSent ? this.formatDateForExcel(booking.paymentlinksentdate) : "Not sent";
        booking.admissionsText = this.admissionsText(booking.admissions);

        this.guests += Number(booking.guests) || 0;
        this.total += Number(booking.total) || 0;
        this.outstanding += Number(booking.outstanding) || 0;
      });

      this.ListBookings = bookings;
      this.busyService.idle();
    }, error => {
      this.busyService.idle();
      this.messageService.add({severity:'error', summary:'Error getting bookings', detail:'An error occurred getting Bookings.'});
    });
  }

  //The line the Booking List shows - "2 x Adults, 1 x Child". The API writes it that way for
  //that report, and it is rebuilt here if the admissions ever arrive as the objects instead.
  private admissionsText(admissions: any): string {
    if (!Array.isArray(admissions)) {
      return admissions == null ? "" : admissions.toString();
    }

    return admissions
      .filter(adm => (Number(adm.noofpeople) || 0) > 0)
      .map(adm => adm.noofpeople + " x " + adm.name)
      .join(", ");
  }

  //A link that has never been sent comes back as the year 1 rather than as nothing, and the
  //app's own unset date is 1900, so anything that early is no date at all.
  private hasBeenSent(sent: any): boolean {
    if (sent == null || sent == "") { return false; }

    const when = new Date(sent);

    return !isNaN(when.getTime()) && when.getUTCFullYear() > 1900;
  }

  /*
    Sends the customer the venue's Payment Link Template for this booking, through
    sendtemplatebooking - the call Edit Booking makes when it sends a template.

    Asked first, every time: it is an email to a customer and cannot be taken back.
  */
  sendPaymentLink(booking: any, event: Event) {
    //The row itself opens the booking.
    event.stopPropagation();

    //The same gate Edit Booking puts in front of sending a template.
    if (localStorage.getItem("BetaEmailing") != "true") {
      this.messageService.add({life: 7000, severity:'warn', summary:'Not Allowed', detail:"Our messaging system is in beta mode. You can turn this on by going to settings and setting 'Try Beta Emailing' to 'Yes' in the Beta section"});
      return;
    }

    if (this.paymentLinkTemplateId() == "") {
      this.messageService.add({life: 7000, severity:'warn', summary:'No template chosen', detail:'Choose a Payment Link Template in Venue Settings before sending payment links.'});
      return;
    }

    if (this.paymentLinkTemplate == null) {
      this.messageService.add({life: 7000, severity:'warn', summary:'Template not found', detail:'The Payment Link Template chosen in Venue Settings could not be found. It may have been disabled or deleted.'});
      return;
    }

    if (booking.email == null || booking.email.toString().trim() == "") {
      this.messageService.add({severity:'warn', summary:'No email address', detail:'This booking has no email address to send a payment link to.'});
      return;
    }

    this.confirmationService.confirm({
      header: "Send Payment Link",
      message: "Send the '" + this.paymentLinkTemplate.name + "' email for " + booking.bookingIdHR + " to " + booking.email + "?",
      accept: () => {
        this.sendingTo = booking.bookingId;

        //The shape Edit Booking posts. There is nowhere on a list to type dynamic text, so
        //none is sent.
        const emailToSend = {
          venueid: this.VenueID,
          bookingid: booking.bookingId,
          templateid: this.paymentLinkTemplate.emailtemplateid,
          dynamicText: [],
          emailsubject: this.paymentLinkTemplate.emailsubject
        };

        this.http.post<any>(bmxurl + "sendtemplatebooking", emailToSend).subscribe(response => {
          this.sendingTo = "";

          if (!response.succeeded) {
            this.messageService.add({severity:'error', summary:'Failure', detail:'An error occurred whilst trying to send the Email, error:' + response.messages[0]});
            return;
          }

          //Shown on the row straight away, so it is plain who has already been chased.
          booking.paymentlinksentdate = new Date().toISOString();
          booking.linkSent = true;
          booking.paymentlinksentdateCSV = this.formatDateForExcel(booking.paymentlinksentdate);
          booking.linkJustSent = true;

          this.messageService.add({severity:'success', summary:'Success', detail:'Payment link sent to ' + booking.email});
        }, error => {
          this.sendingTo = "";
          this.messageService.add({severity:'error', summary:'Failure', detail:'An error occurred whilst trying to send the Email.'});
        });
      }
    });
  }

  formatDateForExcel(dateStr: string): string {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const day = ('0' + d.getUTCDate()).slice(-2);
    const month = ('0' + (d.getUTCMonth() + 1)).slice(-2);
    const year = d.getUTCFullYear();
    const hours = ('0' + d.getUTCHours()).slice(-2);
    const minutes = ('0' + d.getUTCMinutes()).slice(-2);
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }

  //The period cannot run backwards. Moving the From date past the To date takes the To date with
  //it, and the To picker will not open on anything earlier than the From date.
  fromDateChanged() {
    if (this.FromDate == null || this.ToDate == null) {
      return;
    }

    if (new Date(this.ToDate) < new Date(this.FromDate)) {
      this.ToDate = new Date(this.FromDate);
    }
  }
}
